import { BudgetAnalyzerClient } from '@frontend/budget/api';
import { BudgetAnalyzerBudget, BudgetAnalyzerProductAllowance } from '@frontend/budget/types';
import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { IProduct } from '@frontend/product/types';
import type { Dispatch, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface BudgetAvailabilityCardProps {
    accountId: string;
    userId: string;
}

export interface BudgetAvailabilityCardViewProps {
    selectedProduct: IProduct | null;
    setSelectedProduct: Dispatch<IProduct | null>;
    allowance: BudgetAnalyzerProductAllowance | null;
    budgetRows: BudgetAvailabilityBudgetRow[];
    totalUsed: number;
    isLoading: boolean;
    error: ReactNode | null;
}

export interface BudgetAvailabilityBudgetRow extends BudgetAnalyzerBudget {
    used: number;
}

const useBudgetAvailabilityCard = (props: BudgetAvailabilityCardProps): BudgetAvailabilityCardViewProps => {
    const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
    const [allowance, setAllowance] = useState<BudgetAnalyzerProductAllowance | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<ReactNode | null>(null);

    const totalUsed = useMemo(() => allowance?.uses.reduce((total, budgetUse) => total + budgetUse.quantity, 0) ?? 0, [allowance]);
    const budgetRows = useMemo(() => {
        if (!allowance) return [];

        const usedByBudgetId = allowance.uses.reduce((uses, budgetUse) => {
            uses.set(budgetUse.budget_id, (uses.get(budgetUse.budget_id) ?? 0) + budgetUse.quantity);
            return uses;
        }, new Map<string, number>());

        return allowance.budgets.map((budgetEntry) => ({
            ...budgetEntry,
            used: usedByBudgetId.get(budgetEntry.budget.id) ?? 0
        }));
    }, [allowance]);

    const handleProductChange = useCallback((product: IProduct | null) => {
        setSelectedProduct(product);
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (!selectedProduct) {
            setAllowance(null);
            setError(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        BudgetAnalyzerClient.analyzeAccountUserProductAllowance(props.accountId, props.userId, [selectedProduct.id])
            .then((allowanceResponse) => {
                if (cancelled) return;

                setAllowance(allowanceResponse[selectedProduct.id] ?? null);
            })
            .catch((e) => {
                if (cancelled) return;
                ErrorHandler.handleError({ error: e as Error, entity_type: CommonMessage.OBJECTS.BUDGET.DEFAULT });
                setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [props.accountId, props.userId, selectedProduct]);

    return {
        selectedProduct,
        setSelectedProduct: handleProductChange,
        allowance,
        budgetRows,
        totalUsed,
        isLoading,
        error
    };
};

export default useBudgetAvailabilityCard;
